<script setup lang="ts">
import { TabsList, TabsRoot, TabsTrigger } from 'reka-ui';
import type {
  EventHallDTO,
  ProgramItemDTO,
} from '~/shared/api/client/events/events.dto';
import { parseLocalDateTime } from '~/shared/utils/parseLocalDateTime';
import { getMonthName } from '~/shared/utils/formatDate';
import { formatHallCount } from '../lib/formatHallCount';
import EventDetailProgramItem from './EventDetailProgramItem.vue';

const props = defineProps<{
  halls: EventHallDTO[];
}>();

const hallCountLabel = computed(() =>
  formatHallCount(
    props.halls.filter((hall) => hall.program_items.length > 0).length,
  ),
);

const openItems = ref<Set<number>>(new Set());

const toggleItem = (id: number) => {
  const next = new Set(openItems.value);
  if (next.has(id)) {
    next.delete(id);
  } else {
    next.add(id);
  }
  openItems.value = next;
};

const isExpandable = (item: ProgramItemDTO) => {
  return (
    item.speakers.length > 0 || item.moderators.length > 0 || !!item.description
  );
};

interface ProgramDayHall {
  id: number;
  name: string;
  items: ProgramItemDTO[];
}

interface ProgramDay {
  key: string;
  label: string;
  halls: ProgramDayHall[];
}

// A day lists only the halls that have items on that date, in editor order
const days = computed<ProgramDay[]>(() => {
  const byKey = new Map<string, ProgramDay>();
  for (const hall of props.halls) {
    for (const item of hall.program_items) {
      const dt = parseLocalDateTime(item.start_time);
      const key = `${dt.year}-${String(dt.month).padStart(2, '0')}-${String(dt.day).padStart(2, '0')}`;
      const label = `${dt.day} ${getMonthName(dt.month - 1, 'short')} ${dt.year}`;
      let day = byKey.get(key);
      if (!day) {
        day = { key, label, halls: [] };
        byKey.set(key, day);
      }
      let dayHall = day.halls.find((h) => h.id === hall.id);
      if (!dayHall) {
        dayHall = { id: hall.id, name: hall.name, items: [] };
        day.halls.push(dayHall);
      }
      dayHall.items.push(item);
    }
  }
  for (const day of byKey.values()) {
    for (const hall of day.halls) {
      hall.items.sort((a, b) => a.start_time.localeCompare(b.start_time));
    }
  }
  return Array.from(byKey.values()).sort((a, b) => a.key.localeCompare(b.key));
});

// A hall link from an invitation QR code opens that hall on its first day
const linkedHallId = Number(useRoute().query.hall);
const linkedDay = days.value.find((d) =>
  d.halls.some((h) => h.id === linkedHallId),
);

const activeDayKey = ref(linkedDay?.key ?? '');
// String because reka-ui tabs compare values strictly
const activeHallId = ref(linkedDay ? String(linkedHallId) : '');

const activeDay = computed(() =>
  days.value.find((d) => d.key === activeDayKey.value),
);

watchEffect(() => {
  const list = days.value;
  if (list.length === 0) {
    activeDayKey.value = '';
    return;
  }
  if (!list.some((d) => d.key === activeDayKey.value)) {
    activeDayKey.value = list[0]!.key;
  }
});

// Keeps the selected hall when switching days if that day has it too
watchEffect(() => {
  const halls = activeDay.value?.halls ?? [];
  if (!halls.some((h) => String(h.id) === activeHallId.value)) {
    activeHallId.value = halls.length ? String(halls[0]!.id) : '';
  }
});

// On phones the hall row scrolls sideways: bring a partly hidden tab into view
const revealHallTab = (event: MouseEvent) => {
  (event.currentTarget as HTMLElement).scrollIntoView({
    behavior: 'smooth',
    block: 'nearest',
    inline: 'nearest',
  });
};

// The address follows the picked hall, so a hall link can be copied from it
const handleHallChange = (hallId: string | number) => {
  const url = new URL(window.location.href);
  url.searchParams.set('hall', String(hallId));
  window.history.replaceState(window.history.state, '', url);
};

const hasHallTabs = computed(() => (activeDay.value?.halls.length ?? 0) > 1);

const activeItems = computed<ProgramItemDTO[]>(() => {
  return (
    activeDay.value?.halls.find((h) => String(h.id) === activeHallId.value)
      ?.items ?? []
  );
});
</script>

<template>
  <div v-if="days.length">
    <h2 :class="$style.title">
      Программа мероприятия<span v-if="hallCountLabel">
        ({{ hallCountLabel }})</span
      >
    </h2>

    <TabsRoot
      v-if="days.length > 1"
      v-model="activeDayKey"
      :class="[$style.tabs, { [$style.tabsAboveHalls!]: hasHallTabs }]"
    >
      <TabsList aria-label="Дни программы" :class="$style.tabsList">
        <TabsTrigger
          v-for="day in days"
          :key="day.key"
          as="button"
          type="button"
          :value="day.key"
          :class="$style.tab"
        >
          {{ day.label }}
        </TabsTrigger>
      </TabsList>
    </TabsRoot>

    <TabsRoot
      v-if="activeDay && hasHallTabs"
      v-model="activeHallId"
      :class="$style.halls"
      @update:model-value="handleHallChange"
    >
      <TabsList aria-label="Залы" :class="$style.hallsList">
        <TabsTrigger
          v-for="hall in activeDay.halls"
          :key="hall.id"
          as="button"
          type="button"
          :value="String(hall.id)"
          :class="$style.hall"
          @click="revealHallTab"
        >
          {{ hall.name }}
        </TabsTrigger>
      </TabsList>
    </TabsRoot>

    <div :class="$style.list">
      <EventDetailProgramItem
        v-for="item in activeItems"
        :key="item.id"
        :item="item"
        :is-open="openItems.has(item.id)"
        :is-expandable="isExpandable(item)"
        @toggle="toggleItem(item.id)"
      />
    </div>
  </div>
</template>

<style module lang="scss">
.title {
  @include text_h3;
  color: var(--color-black-1);
  margin: 0 0 24px;
}

.tabs {
  display: block;
}

.tabsList {
  display: flex;
  align-items: stretch;
  border: 1px solid var(--color-gray-line);
  border-bottom: none;
}

.tab {
  @include text_caption2_caps;
  font-family: var(--font-golos, 'Golos Text', sans-serif);
  flex: 1 0 0;
  min-width: 0;
  padding: 20px;
  text-align: center;
  color: var(--color-gray-text);
  background: transparent;
  border: none;
  border-right: 1px solid var(--color-gray-line);
  cursor: pointer;

  &:last-child {
    border-right: none;
  }

  &[data-state='active'] {
    background: var(--color-yellow-2);
    color: var(--color-white);
  }
}

.list {
  display: flex;
  flex-direction: column;
}

.halls {
  display: block;
}

// Detached from the list by the hall row, so the day row needs its own bottom line
.tabsAboveHalls .tabsList {
  border-bottom: 1px solid var(--color-gray-line);
}

.tabs + .halls {
  margin-top: 24px;
}

// Wraps on wide screens so every hall stays reachable with a mouse
.hallsList {
  display: flex;
  flex-wrap: wrap;
  padding: 0 20px;
  border: 1px solid var(--color-gray-line);
  border-bottom: none;
  background: var(--color-white);
}

.hall {
  @include text_body2_medium;
  flex-shrink: 0;
  padding: 17px 20px 14px;
  color: var(--color-black-1);
  background: none;
  border: none;
  border-bottom: 3px solid transparent;
  white-space: nowrap;
  cursor: pointer;
  transition:
    color 0.2s,
    border-color 0.2s;

  &:hover {
    color: var(--color-red-1);
  }

  &[data-state='active'] {
    color: var(--color-red-1);
    border-bottom-color: var(--color-red-1);
  }
}

@media (max-width: 1379px) {
  .title {
    font-size: 32px;
    padding-left: 20px;
    padding-right: 20px;
  }

  .tabsList,
  .hallsList {
    border-left: none;
    border-right: none;
  }
}

@media (max-width: 1023px) {
  .title {
    font-size: 24px;
  }
}

@media (max-width: 767px) {
  .hallsList {
    flex-wrap: nowrap;
    overflow-x: auto;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }
}

@media (max-width: 479px) {
  .title {
    font-size: 16px;
  }

  .tab {
    padding: 10px 6px;
  }

  .hallsList {
    padding: 0 8px;
  }

  .tabs + .halls {
    margin-top: 16px;
  }

  .hall {
    padding: 14px 12px 11px;
  }
}
</style>
